import PropTypes from "prop-types";
import CategoryHeaderDescription from "../../../../components/CategoryHeader/CategoryHeaderDescription";
import HorizontalScrollList from "../../../../components/HorizontalScrollList";
import { TRANSLATION_KEY_CATEGORY_CAROUSEL } from "../../../../components/HorizontalScrollList/constants";
import { withTranslation } from "../../../../components/translation";
import {
  TRACKING_DELIMITER,
  TRACKING_QUERY_PARAM,
  TRACKING_VISUAL_CATEGORIES_PREFIX,
  TRACKING_WORD_DELIMITER,
} from "../../../../analytics/constants";
import { formatCategoryBreadcrumbs } from "../../../../helpers/analytics";
import VisualCategoryTile from "./VisualCategoryTile";
import styles from "./VisualCategories.css";

const normalizePathname = (pathname) => {
  if (!pathname) {
    return "";
  }

  return pathname.replace(/\/+$/, "") || "/";
};

const getLinkMetadata = (linkUrl) => {
  if (!linkUrl) {
    return {
      pathname: "",
      cid: undefined,
    };
  }

  const withoutDomain = linkUrl.replace(/^https?:\/\/[^/]+/i, "");
  const [pathname, queryString = ""] = withoutDomain.split("?");
  const cid = new URLSearchParams(queryString).get("cid") || undefined;

  return {
    pathname: normalizePathname(pathname || ""),
    cid,
  };
};

const getSelectedCategoryId = (
  visualCategories,
  currentPath,
  currentSearch,
) => {
  const normalizedCurrentPath = normalizePathname(currentPath);
  const currentCid = new URLSearchParams(currentSearch || "").get("cid");

  const activeCategory = visualCategories.find((category) => {
    if (category.isSeeAll) {
      return false;
    }

    const { pathname, cid } = getLinkMetadata(category.linkUrl);

    if (pathname !== normalizedCurrentPath) {
      return false;
    }

    if (currentCid == null || currentCid === "") {
      return true;
    }

    return cid === String(currentCid);
  });

  if (activeCategory?.id != null) {
    return activeCategory.id;
  }

  const seeAllCategory = visualCategories.find((category) => category.isSeeAll);

  return seeAllCategory?.id ?? visualCategories[0].id;
};

const buildLinkUrlWithTracking = (linkUrl, trackingValue) => {
  if (!linkUrl || !trackingValue) {
    return linkUrl;
  }

  const [pathname, queryString = ""] = linkUrl.split("?");
  const params = new URLSearchParams(queryString);

  if (!params.has(TRACKING_QUERY_PARAM)) {
    params.set(TRACKING_QUERY_PARAM, trackingValue);
  }

  const nextQueryString = params.toString();

  return nextQueryString ? `${pathname}?${nextQueryString}` : pathname;
};

const getTrackingBreadcrumbs = (
  categoryBreadcrumbs,
  visualCategories,
  currentCategoryId,
) => {
  if (!Array.isArray(categoryBreadcrumbs) || categoryBreadcrumbs.length === 0) {
    return [];
  }

  const isVisualCategoryPage = visualCategories.some(
    (category) =>
      !category.isSeeAll && String(category.id) === String(currentCategoryId),
  );

  return isVisualCategoryPage
    ? categoryBreadcrumbs.slice(0, -1)
    : categoryBreadcrumbs;
};

const getTrackingValue = (trackingBreadcrumbs, category) => {
  if (!Array.isArray(trackingBreadcrumbs) || trackingBreadcrumbs.length === 0) {
    return "";
  }

  const categoryTrackingBreadcrumbs = category?.isSeeAll
    ? trackingBreadcrumbs
    : [...trackingBreadcrumbs, { Label: category?.name }];
  const trackingPath = formatCategoryBreadcrumbs(
    categoryTrackingBreadcrumbs,
  ).replaceAll(TRACKING_WORD_DELIMITER, TRACKING_DELIMITER);

  return trackingPath
    ? `${TRACKING_VISUAL_CATEGORIES_PREFIX}${trackingPath}`
    : "";
};

export const VisualCategories = ({
  visualCategories,
  currentPath,
  currentSearch,
  categoryBreadcrumbs,
  currentCategoryId,
  formatTranslation,
}) => {
  if (!visualCategories || visualCategories.length === 0) {
    return null;
  }

  const selectedCategoryId = getSelectedCategoryId(
    visualCategories,
    currentPath,
    currentSearch,
  );
  const trackingBreadcrumbs = getTrackingBreadcrumbs(
    categoryBreadcrumbs,
    visualCategories,
    currentCategoryId,
  );

  const visualCategoriesDescription = (
    <div>
      <CategoryHeaderDescription device="mobileOnly" />
      <CategoryHeaderDescription device="desktopOnly" />
    </div>
  );
  const sectionAriaLabel = formatTranslation(TRANSLATION_KEY_CATEGORY_CAROUSEL);

  return (
    <section
      className={styles.wrapper}
      aria-label={sectionAriaLabel}
      data-visual-categories-section="true"
    >
      <HorizontalScrollList
        className={styles.scrollList}
        isCentralised={false}
        scrollSnapping
        ariaLabel={TRANSLATION_KEY_CATEGORY_CAROUSEL}
        visualCategoriesDescription={visualCategoriesDescription}
        selectedItemSelector='[data-visual-category-selected="true"]'
        selectedItemDependency={selectedCategoryId}
      >
        {visualCategories.map((category) => (
          <VisualCategoryTile
            key={category.id}
            {...category}
            linkUrl={buildLinkUrlWithTracking(
              category.linkUrl,
              getTrackingValue(trackingBreadcrumbs, category),
            )}
            isSelected={selectedCategoryId === category.id}
            onSelect={() => {}}
          />
        ))}
      </HorizontalScrollList>
    </section>
  );
};

VisualCategories.propTypes = {
  formatTranslation: PropTypes.func.isRequired,
  visualCategories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      imageUrl: PropTypes.string.isRequired,
      linkUrl: PropTypes.string.isRequired,
      isSeeAll: PropTypes.bool,
    }),
  ),
  categoryBreadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      Label: PropTypes.string,
      CategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  currentPath: PropTypes.string,
  currentSearch: PropTypes.string,
  currentCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default withTranslation(VisualCategories);
